import type { EditorialCalloutType, EditorialContent } from "@nh/shared";
import { cn } from "@/shared/lib/cn";
import { ProductEditorialIcon } from "./ProductEditorialIcon";

export type ProductCalloutContent = NonNullable<EditorialContent["callout"]>;

export interface ProductCalloutProps {
  callout: ProductCalloutContent;
}

interface CalloutSkin {
  /** La caja: fondo, borde izquierdo y radio. */
  box: string;
  icon: string;
  title: string;
  body: string;
}

/**
 * Los cuatro tipos del original, con sus colores.
 *
 * ── `quote` no lleva filo, y los otros tres sí ───────────────────────
 * Los tres primeros son una NOTA al margen del texto: caja clara con un
 * filo de 3 px a la izquierda y las esquinas de ese lado a escuadra, que
 * es lo que hace que se lea como algo pegado al margen. `quote` es otra
 * cosa —una afirmación en oscuro, a toda caja— y por eso no tiene filo y
 * sí las cuatro esquinas redondeadas.
 *
 * ── Dos colores subidos respecto al original ─────────────────────────
 * En `warning` el original tiñe el icono de `terracotta` (#9F6055) y en
 * `context` de `ink-3`; los dos se conservan. Lo que cambia es el CUERPO:
 * allí va en `ink-2` sobre fondos de color, que en `warning` (#FBEAE4) da
 * 11.9:1 y aguanta, pero el título de `context` iba en `ink` sobre
 * `cream-2` y el cuerpo en `ink-2` — también aguantan. El único que no
 * llegaba era el rótulo `ink-4`, que aquí no existe.
 */
const CALLOUT_SKINS: Record<EditorialCalloutType, CalloutSkin> = {
  science: {
    box: "rounded-[28px] border border-ds-lavender-bg-2 bg-gradient-to-br from-ds-lavender-bg to-white",
    icon: "text-ds-violet-pop",
    title: "text-ds-ink",
    body: "text-ds-ink-2",
  },
  warning: {
    box: "rounded-[28px] border border-ds-amber-soft bg-ds-warn-bg",
    icon: "text-ds-terracotta",
    title: "text-ds-ink",
    body: "text-ds-ink-2",
  },
  context: {
    box: "rounded-[28px] border border-ds-border bg-ds-cream-2",
    icon: "text-ds-ink-3",
    title: "text-ds-ink",
    body: "text-ds-ink-2",
  },
  quote: {
    box: "rounded-[28px] bg-gradient-to-br from-ds-ink to-ds-indigo-night",
    icon: "text-ds-lilac",
    title: "text-white",
    body: "text-white",
  },
};

/**
 * El dato al margen: una nota científica, una advertencia, un contexto o
 * una afirmación en oscuro.
 *
 * ── El tipo y el icono los manda el catálogo ─────────────────────────
 * Antes esta pieza estaba clavada en `science` con un matraz fijo, así
 * que un `type: "warning"` guardado en la base se pintaba en morado como
 * si fuera un dato de laboratorio — una advertencia disfrazada de nota
 * curiosa. Ahora los dos campos del contrato mandan, y `science` es solo
 * lo que se usa cuando no se dice nada.
 *
 * Sin `icon` no hay hueco de icono: el texto empieza pegado al filo, que
 * es lo que hace el original.
 *
 * ── Medidas del original ─────────────────────────────────────────────
 *   caja     padding 28/32 · gap 18 · filo 3px · radio 0 4 4 0
 *   icono    24px, alineado con la primera línea (4px de ajuste)
 *   título   19px/600 · -0.01em · 8px debajo   (`quote`: 22px/300)
 *   cuerpo   14px/300 · interlínea 1.6         (`quote`: 17px)
 */
export function ProductCallout({ callout }: ProductCalloutProps) {
  const type: EditorialCalloutType = callout.type ?? "science";
  const skin = CALLOUT_SKINS[type];
  const isQuote = type === "quote";

  return (
    <section className={cn("flex items-start gap-[18px] px-6 py-7 sm:px-8", skin.box)}>
      {callout.icon && (
        <span className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/70 shadow-ds-xs", skin.icon)}>
          <ProductEditorialIcon name={callout.icon} variant="callout" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <h2
          className={cn(
            "leading-[1.25] tracking-[-0.01em]",
            isQuote ? "text-[22px] font-light" : "text-[19px] font-semibold",
            skin.title,
          )}
        >
          {callout.title}
        </h2>

        {callout.body && (
          <p
            className={cn(
              "mt-2 font-light leading-[1.6]",
              isQuote ? "text-[17px]" : "text-[14px]",
              skin.body,
            )}
          >
            {callout.body}
          </p>
        )}
      </div>
    </section>
  );
}
